import { Box, Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Text } from "@src/appV2/redesign/components/Text";
import { type FormEvent, useState } from "react";
import { useHistory } from "react-router-dom";

interface MobileLoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  keepMeLogin: boolean;
  setKeepMeLogin: (checked: boolean) => void;
  loading: boolean;
  onSubmit: () => Promise<void>;
  verifyingEmail?: boolean;
  disabled?: boolean;
}

/**
 * Mobile-optimized login form using Material UI
 */
export function MobileLoginForm({
  email,
  setEmail,
  keepMeLogin,
  setKeepMeLogin,
  loading,
  onSubmit,
  verifyingEmail = false,
  disabled = false,
}: MobileLoginFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();

  const formLabel = verifyingEmail
    ? "Please enter your email id again to verify login link"
    : "Enter your email to get started";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleKeepMeLoginChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    setKeepMeLogin(checked);
    logEvent(APP_V2_USER_EVENTS.KEEP_ME_LOGGED_IN_CLICKED, { checked });
  }

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Text bold variant="h6" sx={{ textAlign: "center", marginBottom: 1 }}>
        {formLabel}
      </Text>

      <TextField
        fullWidth
        required
        type="email"
        label="Email"
        placeholder="your.email@example.com"
        value={email}
        disabled={disabled || loading}
        autoComplete="email"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "1rem",
          },
        }}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={keepMeLogin}
            disabled={disabled || loading}
            onChange={(event) => {
              handleKeepMeLoginChange(event);
            }}
          />
        }
        label="Keep me logged in for 30 days"
        sx={{ marginLeft: 0 }}
      />

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={disabled || isSubmitting || loading || !email}
        sx={{
          height: 48,
          fontSize: "1rem",
          textTransform: "none",
        }}
      >
        {verifyingEmail ? "Verify Email" : "Get Sign-in Link"}
      </Button>

      {/* Alternative login option */}
      <Box sx={{ textAlign: "center", marginTop: 2 }}>
        <Text variant="body2" sx={{ color: "text.secondary" }}>
          or
        </Text>
        <Button
          variant="text"
          disabled={disabled || loading}
          sx={{ textTransform: "none", marginTop: 1 }}
          onClick={() => {
            history.push("/mobile/login/password");
          }}
        >
          Sign in with Email & Password
        </Button>
      </Box>
    </Box>
  );
}
